import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  type Action,
  Alert,
  backLinkClasses,
  Badge,
  Card,
  DateField,
  Field,
  Icon,
  isNotFound,
  MultiSelect,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  Select,
  StatCard,
  StatCardList,
  Table,
} from '@evogard/ui';
import {
  daysUntil,
  formatDocument,
  formatPhone,
  whatsappAddress,
  type Tone,
} from '@evogard/domain';
import type { Agenda, AgendaMember } from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { type MeasuredClient, useAgendaClient } from './AgendaClient.js';
import { refusalOf } from './common.js';

/**
 * A AGENDA de clientes — os associados da carteira do consultor.
 *
 * ## O que ela responde
 *
 * "Quem são os meus clientes, e como falo com eles": o nome, o nascimento, o
 * telefone, a situação no ERP e a data de contrato mais recente. Uma linha por
 * ASSOCIADO, e não por veículo — o associado com três carros apareceria três
 * vezes com o mesmo telefone. Quantos carros ele tem com o consultor fica na
 * linha de apoio.
 *
 * ## O despacho é o do Painel de Gestão
 *
 * `sales.agenda:ver` abre a própria agenda. Quem tem `ver_qualquer` e ainda não
 * escolheu ninguém cai na TABELA dos consultores da base, e abre a agenda de
 * cada um por ela — sem isso, quem administra e não é consultor abriria a tela
 * na frase "você não está vinculado", que é verdade sobre ele e não responde o
 * que ele veio ver.
 *
 * ## O nascimento quase sempre falta, e por isso não é coluna
 *
 * Medido em 29/09/2026: 1 associado com `data_nascimento` em 52.379 no espelho
 * da CE. Era uma coluna inteira dizendo "não informado" em toda linha; desde
 * 21/10/2026 a data aparece embaixo do nome quando o cadastro a tem, e some
 * quando não tem — o dado continua na tela, sem gastar a largura da tabela.
 *
 * ## A carteira é a de AGORA
 *
 * Os clientes saem de `codigo_voluntario` do veículo, que é o consultor atual,
 * e não o que vendeu. É a pergunta certa para uma agenda; não serve para
 * comissão, que é carimbada no ato.
 */
export function AgendaScreen() {
  const search = useSearch({ from: '/authenticated/sales/portfolio' });
  const navigate = useNavigate();
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const perPage = 10;

  const canAny = can(context, 'sales.agenda', 'ver_qualquer');
  const listing = canAny && search.consultant === undefined;
  /*
   * A busca da LISTA de agendas filtra aqui mesmo: são os consultores da base,
   * que já vieram inteiros — dezenas, não milhares.
   */
  const [listTerm, setListTerm] = useState('');

  /*
   * Lista própria da agenda, e não `useConsultants`: aquela pede
   * `sales.teams:ver`, e quem recebeu só a agenda de todo mundo cairia num 403
   * no meio da barra de filtros.
   */
  const people = useQuery({
    queryKey: ['sales', 'agenda-consultants', tenantId],
    enabled: canAny,
    queryFn: async () => {
      const r = await api.sales.agendaConsultants();
      if (r.status !== 200) throw new Error(refusalOf(r, 'os consultores da carteira'));
      return r.body.consultants;
    },
  });

  const query = useQuery({
    queryKey: [
      'sales',
      'agenda',
      tenantId,
      search.consultant,
      search.search,
      search.situation,
      search.claim,
      search.vehicleSituation,
      search.contractFrom,
      search.contractTo,
      search.dueDay,
      search.page,
    ],
    enabled: !listing,
    queryFn: async () => {
      const r = await api.sales.agenda({
        query: {
          consultant: search.consultant,
          search: search.search,
          situation: search.situation,
          claim: search.claim,
          vehicleSituation: search.vehicleSituation,
          contractFrom: search.contractFrom,
          contractTo: search.contractTo,
          dueDay: search.dueDay,
          page: search.page,
          perPage,
        },
      });
      if (r.status !== 200) throw new Error(refusalOf(r, 'a carteira'));
      return r.body;
    },
    /*
     * A troca de base DESCARTA o que estava na tela: a agenda é de uma pessoa
     * numa praça, e mostrar os clientes da praça anterior sob o nome da nova
     * seria a tela afirmando o que o código não cumpre.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const data = query.data;
  const measured =
    data?.estado === 'medido'
      ? (data as Extract<Agenda, { estado: 'medido' }>)
      : undefined;

  const consultants = (people.data ?? []).filter((p) => {
    const term = folded(listTerm.trim());
    return (
      term === '' ||
      folded(p.nome ?? '').includes(term) ||
      p.erpVolunteerCode.includes(listTerm.trim())
    );
  });

  return (
    <Page measure="cheia">
      {canAny && !listing && (
        <Link to="/sales/portfolio" search={{ page: 1 }} className={backLinkClasses()}>
          <Icon nome="seta-esquerda" size={16} />
          Todas as carteiras
        </Link>
      )}
      <PageHeader
        title={
          measured?.consultant.ofAnotherConsultant
            ? `Carteira de ${measured.consultant.nome ?? `consultor ${measured.consultant.erpVolunteerCode}`}`
            : 'Carteira'
        }
      >
        {listing
          ? 'A carteira de cada consultor da base. Abra uma para ver os associados.'
          : undefined}
      </PageHeader>

      {listing && (
        <>
          {people.isError && <Alert tone="erro">{people.error.message}</Alert>}
          <Table
            framed
            label="Carteiras da base"
            search={{
              label: 'Pesquisar consultores',
              placeholder: 'Nome ou código do consultor',
              value: listTerm,
              onChange: setListTerm,
            }}
            items={consultants}
            loading={people.isLoading}
            rowKey={(p) => p.erpVolunteerCode}
            empty={
              listTerm.trim() !== ''
                ? `Nenhum consultor casa com "${listTerm.trim()}".`
                : 'Nenhum consultor vinculado nesta base. Quem vincula é Comercial › Consultores e times.'
            }
            rowTitle={(p) => p.nome ?? `Consultor ${p.erpVolunteerCode}`}
            rowSubtitle={(p) => `Código ${p.erpVolunteerCode} nesta base`}
            rowIcon="cracha"
            rowActions={(p): Action[] => [
              {
                label: 'Ver a carteira',
                icon: 'olho',
                run: () =>
                  void navigate({
                    to: '/sales/portfolio',
                    search: { consultant: p.erpVolunteerCode, page: 1 },
                  }),
              },
            ]}
            columns={[
              {
                title: 'Consultor',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (p) => p.nome ?? <NotProvided>sem nome</NotProvided>,
              },
              { title: 'Código', minWidth: '5.5rem', cell: (p) => p.erpVolunteerCode },
            ]}
          />
        </>
      )}

      {!listing && query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {!listing && data?.estado === 'sem_vinculo' && (
        <Alert tone="alerta">{data.message}</Alert>
      )}

      {!listing && (query.isLoading || measured) && (
        <>
          {/* Os dois totais do recorte (28/10/2026, a referência do dono). */}
          <StatCardList stretch>
            <StatCard
              icon="pessoas"
              label="Total de associados"
              value={
                measured && (
                  <span className="tabular-nums">
                    {measured.total.toLocaleString('pt-BR')}
                  </span>
                )
              }
            />
            <StatCard
              icon="veiculo"
              label="Total de veículos"
              value={
                measured && (
                  <span className="tabular-nums">
                    {measured.vehiclesTotal.toLocaleString('pt-BR')}
                  </span>
                )
              }
            />
          </StatCardList>

          <AgendaFilters
            /* Um por consultor: trocar de carteira recria a busca, sem o termo da anterior. */
            key={search.consultant ?? 'propria'}
            search={search}
            measured={measured}
            consultants={canAny ? (people.data ?? []) : null}
            onChange={(next) =>
              void navigate({
                to: '/sales/portfolio',
                /* Filtro novo volta à primeira página. */
                search: (prev) => ({ ...prev, ...next, page: 1 }),
                replace: true,
              })
            }
          />

          <AgendaReport
            measured={measured}
            loading={query.isLoading}
            filtered={Boolean(
              search.search ||
              search.situation ||
              search.claim ||
              search.vehicleSituation ||
              search.contractFrom ||
              search.contractTo ||
              search.dueDay,
            )}
            consultant={search.consultant}
            onPage={(page) =>
              void navigate({ to: '/sales/portfolio', search: { ...search, page } })
            }
            onOpen={(memberCode) =>
              void navigate({
                to: '/sales/portfolio/members/$code',
                params: { code: memberCode },
                search: { consultant: search.consultant },
              })
            }
          />
        </>
      )}
    </Page>
  );
}

type Measured = Extract<Agenda, { estado: 'medido' }>;
type AgendaSearch = {
  search?: string;
  situation?: string;
  claim?: 'em_tratativa';
  vehicleSituation?: string;
  contractFrom?: string;
  contractTo?: string;
  dueDay?: string;
  consultant?: string;
};

/** O recorte de lista do endereço — `"A,B"` — como a lista marcada. */
function listOf(value: string | undefined): string[] {
  return value === undefined ? [] : value.split(',');
}

/**
 * Os FILTROS, num cartão acima do relatório (28/10/2026, a referência do dono):
 * a busca na largura toda e, embaixo, os recortes — situação do associado,
 * situação do carro, contrato de/até e dia de vencimento, mais o sinistro e,
 * para quem abre a carteira de outros, o consultor.
 *
 * Situação do associado, situação do carro e dia de vencimento aceitam VÁRIOS
 * valores (`MultiSelect`, 29/10/2026); dentro de um recorte vale qualquer um.
 * Cada recorte vale ao escolher; a busca espera a pessoa parar de digitar — a
 * carteira passa de três mil clientes e a busca vai ao servidor, e sem a espera
 * cada letra seria uma consulta. As opções vêm da carteira INTEIRA, sem o
 * recorte: com ele, o seletor perderia a opção que acabou de ser escolhida.
 */
function AgendaFilters({
  search,
  measured,
  consultants,
  onChange,
}: {
  search: AgendaSearch;
  measured: Measured | undefined;
  /** `null` para quem só abre a própria carteira. */
  consultants: { erpVolunteerCode: string; nome: string | null }[] | null;
  onChange: (next: Partial<AgendaSearch>) => void;
}) {
  const [term, setTerm] = useState(search.search ?? '');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const type = (value: string) => {
    setTerm(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => onChange({ search: value.trim() || undefined }),
      400,
    );
  };
  const pick = (key: keyof AgendaSearch) => (value: string) =>
    onChange({ [key]: value || undefined });
  /*
   * Os recortes de lista marcam VÁRIOS valores (29/10/2026, pedido do dono), e
   * viajam juntos por vírgula no endereço e na API. Nada marcado é sem filtro.
   */
  const pickMany =
    (key: 'situation' | 'vehicleSituation' | 'dueDay') => (values: string[]) =>
      onChange({ [key]: values.length > 0 ? values.join(',') : undefined });

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <Field
          label="Buscar associado"
          placeholder="Nome, CPF, placa, telefone ou e-mail"
          value={term}
          onChange={(e) => type(e.target.value)}
        />
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48">
            {/*
             * Pela DESCRIÇÃO, e não pelo código: `codigo_situacao` vem vazio
             * no espelho da CE. O grupo sem descrição não vira opção.
             */}
            <MultiSelect
              label="Situação do associado"
              placeholder="Todas as situações"
              options={(measured?.situations ?? []).flatMap((s) =>
                s.description === null
                  ? []
                  : [{ value: s.description, label: s.description }],
              )}
              selected={listOf(search.situation)}
              onChange={pickMany('situation')}
            />
          </div>
          <div className="w-48">
            <MultiSelect
              label="Situação do veículo"
              placeholder="Todas as situações"
              options={(measured?.vehicleSituations ?? []).map((s) => ({
                value: s.code,
                label: s.description ?? `Situação ${s.code}`,
              }))}
              selected={listOf(search.vehicleSituation)}
              onChange={pickMany('vehicleSituation')}
            />
          </div>
          <div className="w-44">
            <DateField
              label="Contrato — de"
              value={search.contractFrom ?? ''}
              onChange={pick('contractFrom')}
            />
          </div>
          <div className="w-44">
            <DateField
              label="Contrato — até"
              value={search.contractTo ?? ''}
              onChange={pick('contractTo')}
            />
          </div>
          <div className="w-40">
            <MultiSelect
              label="Dia de vencimento"
              placeholder="Todos os dias"
              options={(measured?.dueDays ?? []).map((d) => ({
                value: d,
                label: `Dia ${d}`,
              }))}
              selected={listOf(search.dueDay)}
              onChange={pickMany('dueDay')}
            />
          </div>
          <div className="w-48">
            <Select
              label="Sinistro"
              value={search.claim ?? ''}
              onChange={(e) =>
                onChange({
                  claim: e.target.value === 'em_tratativa' ? 'em_tratativa' : undefined,
                })
              }
            >
              <option value="">Todos os associados</option>
              <option value="em_tratativa">Em tratativa no sinistro</option>
            </Select>
          </div>
          {consultants !== null && (
            <div className="w-56">
              <Select
                label="Consultor"
                value={search.consultant ?? ''}
                onChange={(e) => pick('consultant')(e.target.value)}
              >
                <option value="">Todas as carteiras</option>
                {consultants.map((p) => (
                  <option key={p.erpVolunteerCode} value={p.erpVolunteerCode}>
                    {p.nome ?? `Consultor ${p.erpVolunteerCode}`}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * O RELATÓRIO: um associado por linha — nome e CPF, contato, situação e
 * quantos carros ele tem com o consultor —, e a linha abre os carros dele ali
 * mesmo (`AgendaVehicles`).
 */
function AgendaReport({
  measured,
  loading,
  filtered,
  consultant,
  onPage,
  onOpen,
}: {
  measured: Measured | undefined;
  loading: boolean;
  filtered: boolean;
  /** De quem é a carteira aberta — o carro aberto na linha é desta carteira. */
  consultant: string | undefined;
  onPage: (page: number) => void;
  onOpen: (memberCode: string) => void;
}) {
  return (
    <Card title="Relatório" bleed>
      <Table
        paginate={false}
        label="Associados da carteira"
        items={measured?.items ?? []}
        loading={loading}
        rowKey={(m) => m.code}
        expand={(m) => <AgendaVehicles member={m} consultant={consultant} />}
        expandOnRowClick
        rowTitle={(m) => m.name ?? `Associado ${m.code}`}
        rowActions={(m): Action[] => [
          {
            label: 'Abrir a ficha em página própria',
            icon: 'olho',
            run: () => onOpen(m.code),
          },
        ]}
        empty={
          filtered
            ? 'Nenhum associado com esses filtros.'
            : 'Nenhum veículo do ERP aponta para este consultor, então não há associado na carteira.'
        }
        columns={[
          {
            title: 'Associado',
            alignment: 'inicio' as const,
            minWidth: '16rem',
            cell: (m) => <MemberCell member={m} />,
          },
          {
            title: 'Contato',
            alignment: 'inicio' as const,
            minWidth: '14rem',
            cell: (m) => <ContactCell member={m} />,
          },
          {
            title: 'Situação',
            alignment: 'inicio' as const,
            minWidth: '9rem',
            cell: (m) =>
              m.status.description === null ? (
                <NotProvided>não informada</NotProvided>
              ) : (
                <Badge tone={situationTone(m.status.description)}>
                  {m.status.description}
                </Badge>
              ),
          },
          {
            title: 'Veículos',
            minWidth: '6rem',
            cell: (m) => <CountPill value={m.vehicles} />,
          },
        ]}
      />

      {measured && (
        <Pagination
          page={measured.page}
          perPage={measured.perPage}
          total={measured.total}
          onChange={onPage}
          label="associados da carteira"
          className="border-t border-borda-suave px-5 py-4"
        />
      )}
    </Card>
  );
}

/**
 * Os carros de um associado, dentro da linha dele (28/10/2026, a referência do
 * dono): placa, modelo e ano, plano, situação, mensalidade, vencimento, há
 * quantos dias está em atraso e atendimentos — e a seta que abre a ficha do
 * carro.
 *
 * A MENSALIDADE é o valor do último boleto que cobre o carro (decidido pelo
 * dono): o cadastro não tem um campo de mensalidade, e o último boleto é o que
 * o associado de fato paga por mês. Sem boleto nos doze meses, "—". O
 * VENCIMENTO é o dia do próprio carro, da lista `vencimento` do SGA.
 *
 * A consulta é a da ficha (`useAgendaClient`): abrir a linha e depois a ficha
 * não pergunta duas vezes.
 */
function AgendaVehicles({
  member,
  consultant,
}: {
  member: AgendaMember;
  consultant: string | undefined;
}) {
  const query = useAgendaClient(member.code, consultant);
  const navigate = useNavigate();

  if (isNotFound(query.error)) {
    return (
      <Alert tone="alerta">
        {query.error.message}: não há veículo dele com este consultor.
      </Alert>
    );
  }
  if (query.isError) return <Alert tone="erro">{query.error.message}</Alert>;
  if (query.data?.estado === 'sem_vinculo') {
    return <Alert tone="alerta">{query.data.message}</Alert>;
  }
  const detail = query.data as MeasuredClient | undefined;
  type Vehicle = MeasuredClient['vehicles'][number];
  const monthlyOf = (v: Vehicle) =>
    detail?.boletos.find((b) => b.vehicleCodes.includes(v.code))?.amount ?? null;
  const lateOf = (v: Vehicle) =>
    detail === undefined ? null : daysLate(detail.boletos, v.code);
  const firstName = (member.name ?? '').trim().split(/\s+/)[0] ?? '';

  return (
    <section className="flex flex-col gap-2">
      <h3 className="rotulo text-texto-suave">
        Veículos vinculados{firstName ? ` — ${firstName}` : ''}
      </h3>
      <Table<Vehicle>
        framed
        paginate={false}
        label={`Veículos de ${member.name ?? member.code}`}
        items={detail?.vehicles ?? []}
        loading={query.isLoading}
        rowKey={(v) => v.code}
        /*
         * A linha inteira abre a ficha do carro, como antes do redesenho
         * (28/10/2026, pedido do dono); a seta no fim continua sendo o caminho
         * do teclado.
         */
        onRowClick={(v) =>
          void navigate({
            to: '/sales/portfolio/members/$code',
            params: { code: member.code },
            search: { consultant, vehicle: v.code, view: 'boletos' },
            state: { backLabel: 'Carteira' },
          })
        }
        empty="Nenhum veículo deste associado com este consultor."
        columns={[
          {
            title: 'Placa',
            minWidth: '7rem',
            cell: (v) =>
              v.plate === null ? (
                <NotProvided>sem placa</NotProvided>
              ) : (
                <span className="rounded-borda border border-borda bg-superficie-suave px-1.5 py-0.5 font-mono text-xs">
                  {v.plate}
                </span>
              ),
          },
          {
            title: 'Modelo / ano',
            alignment: 'inicio' as const,
            minWidth: '12rem',
            cell: (v) => (
              <span>
                <span className="font-medium text-texto">
                  {[v.brand, v.model].filter(Boolean).join(' ') || '—'}
                </span>
                {v.modelYear && (
                  <span className="text-texto-suave"> · {v.modelYear}</span>
                )}
              </span>
            ),
          },
          {
            title: 'Plano',
            alignment: 'inicio' as const,
            minWidth: '8rem',
            cell: (v) => v.plan ?? <NotProvided>—</NotProvided>,
          },
          {
            title: 'Situação',
            alignment: 'inicio' as const,
            minWidth: '8rem',
            cell: (v) =>
              v.status.description === null ? (
                <NotProvided>—</NotProvided>
              ) : (
                <Badge tone={situationTone(v.status.description)}>
                  {v.status.description}
                </Badge>
              ),
          },
          {
            title: 'Mensalidade',
            alignment: 'fim' as const,
            minWidth: '7rem',
            cell: (v) => {
              const amount = monthlyOf(v);
              return amount === null ? (
                <NotProvided>—</NotProvided>
              ) : (
                <span className="font-semibold">
                  <MoneyValue value={amount} />
                </span>
              );
            },
          },
          {
            title: 'Vencimento',
            minWidth: '6rem',
            cell: (v) => (v.dueDay ? `dia ${v.dueDay}` : <NotProvided>—</NotProvided>),
          },
          {
            title: 'Em atraso',
            minWidth: '7rem',
            cell: (v) => {
              const late = lateOf(v);
              if (late !== null) {
                return <Badge tone="erro">{late === 1 ? '1 dia' : `${late} dias`}</Badge>;
              }
              /* Varredura de boletos parcial: "em dia" seria afirmar sem saber. */
              return detail?.boletosPartial ? (
                <NotProvided>—</NotProvided>
              ) : (
                <span className="text-texto-suave">em dia</span>
              );
            },
          },
          {
            title: 'Atend.',
            minWidth: '4rem',
            cell: (v) => <CountPill value={v.serviceRecords} />,
          },
          {
            title: '',
            minWidth: '3rem',
            cell: (v) => (
              <Link
                to="/sales/portfolio/members/$code"
                params={{ code: member.code }}
                search={{ consultant, vehicle: v.code, view: 'boletos' }}
                state={{ backLabel: 'Carteira' }}
                aria-label={`Abrir a ficha de ${v.plate ?? 'veículo'}`}
                className="grid size-8 place-items-center rounded-full border border-borda text-texto-suave transition-colors hover:border-borda-forte hover:text-texto"
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            ),
          },
        ]}
      />
    </section>
  );
}

/**
 * HÁ QUANTOS DIAS o carro está em atraso — 29/10/2026, no lugar da data de
 * contrato, quando "Recuperar recorrência" saiu das Métricas para cá.
 *
 * Conta do vencimento do boleto em aberto MAIS ANTIGO do carro até hoje, pela
 * mesma `daysUntil` que a tabela das Métricas usava: vencido em 20/09 e visto
 * em 22/09 são 2 dias. O vencimento é o ATUAL — o prorrogado conta da nova
 * data, que é a que o associado combinou.
 *
 * "Em aberto" é `delinquent` (`considerado_inadimplencia` do SGA), e não
 * `paid === false`: CANCELADO e BAIXADO C/ DESCONTO também não são pagos, e
 * contá-los inventaria a dívida. O boleto que vence hoje ainda não atrasou.
 *
 * A ficha lê doze meses de boletos (`dueFrom`): o atraso de mais de um ano
 * aparece como o do boleto mais velho da janela, e é piso.
 */
function daysLate(
  boletos: MeasuredClient['boletos'],
  vehicleCode: string,
): number | null {
  let oldest: string | null = null;
  for (const b of boletos) {
    if (b.delinquent !== true || b.dueDate === null) continue;
    if (!b.vehicleCodes.includes(vehicleCode)) continue;
    if (oldest === null || b.dueDate < oldest) oldest = b.dueDate;
  }
  if (oldest === null) return null;
  const late = -daysUntil(oldest);
  return late > 0 ? late : null;
}

/**
 * O tom do selo de situação — só pela palavra que o ERP escreve, e só para as
 * três que dizem por si: ATIVO é verde; INADIMPLENTE e CANCELADO, vermelho. O
 * resto fica cinza. Não é classificação da casa (regra 14): nenhuma conta e
 * nenhum filtro dependem disso, é a cor da palavra.
 */
function situationTone(description: string): Tone {
  const word = folded(description.trim());
  if (word === 'ativo' || word === 'ativa') return 'sucesso';
  if (word.includes('inadimpl') || word.includes('cancelad')) return 'erro';
  return 'neutro';
}

/** O número numa pastilha redonda — veículos e atendimentos. */
function CountPill({ value }: { value: number }) {
  return (
    <span className="inline-grid min-w-6 place-items-center rounded-full bg-superficie-suave px-1.5 py-0.5 text-xs font-semibold tabular-nums text-texto">
      {value.toLocaleString('pt-BR')}
    </span>
  );
}

/** O aviso do destaque — o mesmo texto da página da placa. */
const CLAIM_NOTICE =
  'Este associado tem veículo em tratativa com o sinistro. Abra a placa para ver a fase.';

/**
 * O associado: o nome e, embaixo, o CPF (ou CNPJ). Com carro desta carteira em
 * tratativa no sinistro, o selo "Em sinistro" ao lado do nome.
 *
 * Nome nulo é o cadastro que o espelho ainda não tem — o carro aponta para o
 * associado, e o cadastro dele não sincronizou. Diz isso, em vez de um
 * travessão que o leitor teria que interpretar.
 */
function MemberCell({ member }: { member: AgendaMember }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-semibold text-texto">
          {member.name ?? <NotProvided>cadastro não sincronizado</NotProvided>}
        </span>
        {member.inClaim && (
          <span title={CLAIM_NOTICE} className="shrink-0">
            <Badge tone="alerta">Em sinistro</Badge>
          </span>
        )}
      </span>
      {member.document && (
        <span className="truncate text-xs text-texto-suave tabular-nums">
          {formatDocument(member.document)}
        </span>
      )}
    </span>
  );
}

/**
 * O contato: o telefone — que abre o WhatsApp quando é um número que dá para
 * discar — e, embaixo, o e-mail. `formatPhone` só mascara o número válido; o
 * resto sai como veio, porque mascarar um número inválido o faria parecer
 * válido.
 */
function ContactCell({ member }: { member: AgendaMember }) {
  const raw = member.phone ? `${member.phone.ddd ?? ''}${member.phone.number}` : null;
  const address = whatsappAddress(raw);
  return (
    <span className="flex min-w-0 flex-col gap-0.5 text-xs">
      {raw === null ? (
        <NotProvided>sem telefone</NotProvided>
      ) : address === null ? (
        <span className="flex items-center gap-1.5 tabular-nums">
          <Icon nome="telefone" size={16} className="text-texto-suave" />
          {formatPhone(raw)}
        </span>
      ) : (
        <a
          href={`https://wa.me/${address.slice(1)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 font-medium text-sucesso tabular-nums hover:underline"
        >
          <Icon nome="telefone" size={16} />
          {formatPhone(raw)}
        </a>
      )}
      {member.email && (
        <span className="flex min-w-0 items-center gap-1.5 text-texto-suave">
          <Icon nome="email" size={16} className="shrink-0" />
          <span className="truncate">{member.email}</span>
        </span>
      )}
    </span>
  );
}

/** Sem acento e em minúscula — "joao" acha "João". */
function folded(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
